import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Send, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../context/AuthContext'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function DiscussionModal({ open, transactionId, onClose }) {
  const { token } = useAuth()
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const messagesEndRef = useRef(null)
  const intervalRef = useRef(null)
  
  useEffect(() => {
    if (open && transactionId) {
      fetchMessages()
      // Poll for new messages every 5 seconds
      intervalRef.current = setInterval(fetchMessages, 5000)
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
      }
    }
  }, [open, transactionId])
  
  useEffect(() => {
    scrollToBottom()
  }, [messages])
  
  const fetchMessages = async () => {
    if (!transactionId) return
    
    setIsLoading(true)
    try {
      const response = await axios.get(`${API}/discussion/${transactionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
      setMessages(response.data.messages || [])
    } catch (err) {
      console.error('Failed to load messages:', err)
    } finally {
      setIsLoading(false)
    }
  }
  
  const sendMessage = async () => {
    if (!newMessage.trim()) return
    
    if (!token) {
      toast.error('Please connect your wallet to participate in discussion')
      return
    }
    
    setIsSending(true)
    try {
      await axios.post(
        `${API}/discussion/${transactionId}`,
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      
      setNewMessage('')
      fetchMessages()
      toast.success('Message sent!')
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Giver and receiver cannot participate in discussion')
      } else {
        toast.error(err.response?.data?.detail || 'Failed to send message')
      }
    } finally {
      setIsSending(false)
    }
  }
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Anonymous Discussion</DialogTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Discuss this transaction anonymously. Giver and receiver cannot participate.
          </p>
        </DialogHeader>
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg max-h-96 min-h-[300px]">
          {isLoading && messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400">
              No messages yet. Start the discussion!
            </p>
          ) : (
            messages.map(msg => (
              <div key={msg.id} className="bg-white dark:bg-gray-700 p-3 rounded-lg shadow-sm">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                    {msg.anonymous_id}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(msg.created_at).toLocaleTimeString()}
                  </span>
                </div>
                <p className="text-sm text-gray-800 dark:text-gray-200">{msg.message}</p>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div className="space-y-2">
          {!token && (
            <p className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
              Connect your wallet to join the discussion
            </p>
          )}
          <div className="flex gap-2">
            <Input
              placeholder={token ? "Type your message..." : "Connect wallet to send messages"}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              maxLength={500}
              disabled={isSending || !token}
            />
            <Button 
              onClick={sendMessage} 
              disabled={isSending || !newMessage.trim() || !token}
            >
              {isSending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {newMessage.length}/500 characters
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
